import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc, writeBatch } from 'firebase/firestore';
import { toMs } from '../../office/today/collections';
import {
  ACTIONS,
  GATE_LABELS,
  LOAN_REQUIRED_DEP_KEY,
  ROLE_OPTIONS,
  STAGE_OPTIONS,
  UNIT_BOOKED_DEP_KEY,
  actionInfo,
  isGate,
  loanPreset,
  makeStep,
  normalizeIncomingSteps,
  normalizeKey,
  salesPreset,
  stagesFor,
  stepsForSave,
  validateWorkflow,
  withDeedPossessionFlow,
  type WfStep,
} from './workflowModel';
import { FlowChart } from './FlowChart';
import { BUC_DEP_KEY, FULL_PAYMENT_DEP_KEY, PRE_BUC_DEP_KEY } from '../../office/deedPossessionGates';
import { yesAnswerDependency, yesAnswerStepKey } from '../../office/workflowAnswerGate';
import '../../office/today/today.css';
import './workflowStudio.css';

type Props = {
  db: any;
  currentUser: any;
  projects: any[];
  canEdit: boolean;
  docTemplateOptions?: string[];
  emailTemplateOptions?: string[];
  uploadDocKeyOptions?: string[];
};

type Draft = { name: string; type: 'main' | 'loan'; version: number; active: boolean; description: string; assignedProjectIds: string[]; steps: WfStep[] };
const EMPTY: Draft = { name: '', type: 'main', version: 1, active: true, description: '', assignedProjectIds: [], steps: [] };
const fromRow = (row: any): Draft => ({
  name: String(row?.name || ''),
  type: String(row?.type || 'main') === 'loan' ? 'loan' : 'main',
  version: Number(row?.version || 1),
  active: row?.active !== false,
  description: String(row?.description || ''),
  assignedProjectIds: Array.isArray(row?.assignedProjectIds) ? row.assignedProjectIds : [],
  steps: normalizeIncomingSteps(row?.steps),
});
const sameDraft = (a: Draft, b: Draft) => JSON.stringify({ ...a, steps: stepsForSave(a.steps) }) === JSON.stringify({ ...b, steps: stepsForSave(b.steps) });
const dateTime = (ms: number) => new Date(ms).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

const Ic = ({ d, size = 15 }: { d: React.ReactNode; size?: number }) => (
  <svg className="ot-ic" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
const I = {
  up: <path d="m6 15 6-6 6 6" />,
  down: <path d="m6 9 6 6 6-6" />,
  copy: <><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></>,
  trash: <><path d="M4 7h16M10 11v6M14 11v6" /><path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  grip: <><circle cx="9" cy="6" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="18" r="1" /></>,
  x: <path d="M6 6l12 12M18 6 6 18" />,
  lock: <><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  gear: <><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" /></>,
};

/** Office › Settings › Workflows: the editor for the step-by-step office workflow every booked unit follows. */
export default function WorkflowStudio({ db, currentUser, projects, canEdit, docTemplateOptions = [], emailTemplateOptions = [], uploadDocKeyOptions = [] }: Props) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [base, setBase] = useState<Draft>(EMPTY);
  const [stepIdx, setStepIdx] = useState(0);
  const [view, setView] = useState<'stage' | 'order' | 'flow'>('stage');
  const [expanded, setExpanded] = useState(false);
  const [panel, setPanel] = useState<'step' | 'details' | 'history'>('step');
  const [busy, setBusy] = useState<'' | 'save' | 'publish'>('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [keyUnlocked, setKeyUnlocked] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [newMenu, setNewMenu] = useState(false);
  // Each column scrolls on its own, so it must end at the bottom of the screen: measure where the grid starts.
  const gridRef = useRef<HTMLDivElement>(null);
  const [gridH, setGridH] = useState(0);
  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const fit = () => {
      const top = el.getBoundingClientRect().top;
      setGridH(Math.max(320, Math.round(window.innerHeight - Math.max(0, top) - 16)));
    };
    fit();
    const ro = new ResizeObserver(fit);
    const root = el.parentElement;
    if (root) Array.from(root.children).forEach((c) => c !== el && ro.observe(c));
    window.addEventListener('resize', fit);
    return () => { ro.disconnect(); window.removeEventListener('resize', fit); };
  }, [msg, loading]);

  useEffect(() => {
    const q = query(collection(db, 'officeWorkflowDefinitions'), orderBy('updatedAt', 'desc'));
    return onSnapshot(
      q,
      (snap) => {
        setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setLoading(false);
      },
      (err) => {
        setLoadError(err?.message || "Couldn't load workflows.");
        setLoading(false);
      }
    );
  }, [db]);

  const row = rows.find((r) => r.id === selectedId) || null;
  // Open the first workflow once the list arrives.
  useEffect(() => {
    if (selectedId || !rows.length || draft.steps.length || draft.name) return;
    const first = rows.find((r) => String(r.type) !== 'loan') || rows[0];
    open(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const open = (r: any) => {
    const d = fromRow(r);
    setSelectedId(String(r.id));
    setDraft(d);
    setBase(d);
    setStepIdx(0);
    setPanel('step');
    setMsg(null);
    setConfirmDelete(false);
    setKeyUnlocked(false);
  };
  const startNew = (d: Draft) => {
    setSelectedId('');
    setDraft(d);
    setBase(EMPTY);
    setStepIdx(0);
    setPanel('details');
    setMsg(null);
    setNewMenu(false);
  };

  const dirty = !sameDraft(draft, base) || !selectedId;
  const publishedAt = toMs(row?.publishedAt);
  const savedNotPublished = !!row && !!publishedAt && toMs(row.updatedAt) > publishedAt + 1500;
  const projectName = useMemo(() => new Map((projects || []).map((p: any) => [String(p.id), String(p.name || p.id)])), [projects]);
  const projectNames = draft.assignedProjectIds.map((id) => projectName.get(id) || id);
  const errors = useMemo(() => validateWorkflow(draft.name, draft.steps), [draft.name, draft.steps]);
  const stages = useMemo(() => stagesFor(draft.steps, draft.type), [draft.steps, draft.type]);
  const step = draft.steps[Math.min(stepIdx, Math.max(0, draft.steps.length - 1))] || null;
  const publishedKeys = useMemo(() => new Set(normalizeIncomingSteps(row?.steps || []).map((s) => s.key)), [row]);
  const keyLocked = !!step && !!row && publishedKeys.has(base.steps[stepIdx]?.key || '') && base.steps[stepIdx]?.key === step.key && !keyUnlocked;

  const setSteps = (fn: (s: WfStep[]) => WfStep[]) => setDraft((d) => ({ ...d, steps: fn(d.steps) }));
  const patchStep = (patch: Partial<WfStep>) => setSteps((all) => all.map((s, i) => (i === stepIdx ? { ...s, ...patch } : s)));
  const select = (i: number) => {
    setStepIdx(i);
    setPanel('step');
    setConfirmDelete(false);
    setKeyUnlocked(false);
  };
  const move = (from: number, to: number) => {
    if (to < 0 || to >= draft.steps.length || from === to) return;
    setSteps((all) => {
      const next = all.slice();
      const [it] = next.splice(from, 1);
      next.splice(to, 0, it);
      return next;
    });
    setStepIdx(to);
  };
  const addStep = () => {
    const n = makeStep(`Step ${draft.steps.length + 1}`);
    const taken = new Set(draft.steps.map((s) => s.key));
    let key = n.key;
    for (let i = 2; taken.has(key); i += 1) key = `${n.key}_${i}`;
    setSteps((all) => [...all.slice(0, stepIdx + 1), { ...n, key, stage: step?.stage || '' }, ...all.slice(stepIdx + 1)]);
    select(Math.min(draft.steps.length, stepIdx + 1));
  };
  const duplicate = () => {
    if (!step) return;
    const c: WfStep = JSON.parse(JSON.stringify(step));
    c.key = normalizeKey(`${c.key || 'step'}_copy`);
    setSteps((all) => [...all.slice(0, stepIdx + 1), c, ...all.slice(stepIdx + 1)]);
    select(stepIdx + 1);
  };
  // The remove confirmation sits at the end of the form; bring it into view when asked from the header.
  useEffect(() => {
    if (confirmDelete) window.setTimeout(() => document.querySelector('.ows-confirm')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 30);
  }, [confirmDelete]);

  const remove = () => {
    if (!step) return;
    const gone = step.key;
    // Also take it out of every "Ready when" list so the workflow stays valid.
    setSteps((all) => all.filter((_, i) => i !== stepIdx).map((s) => ({ ...s, dependsOnSteps: s.dependsOnSteps.filter((d) => d !== gone) })));
    setStepIdx((i) => Math.max(0, Math.min(i - 1, draft.steps.length - 2)));
    setConfirmDelete(false);
  };

  // What a step may wait for: gates, earlier steps, and steps the other workflow shares.
  const otherType = draft.type === 'loan' ? 'main' : 'loan';
  const otherRow = rows.find((r) => String(r.type || '') === otherType && r.active !== false);
  const otherSteps = useMemo(() => normalizeIncomingSteps(otherRow?.steps || []).filter((s) => s.showInOtherWorkflow), [otherRow]);
  const depLabel = (key: string) => {
    if (GATE_LABELS[key]) return GATE_LABELS[key];
    const answerStepKey = yesAnswerStepKey(key);
    if (answerStepKey) return `Yes to ${draft.steps.find((s) => s.key === answerStepKey)?.title || answerStepKey}`;
    if (key.includes(':')) {
      const [t, k] = key.split(':');
      const s = otherSteps.find((x) => x.key === k);
      return `${s?.title || k} (${t === 'loan' ? 'loan' : 'sales'})`;
    }
    return draft.steps.find((s) => s.key === key)?.title || key;
  };
  const depOptions = step
    ? [
        UNIT_BOOKED_DEP_KEY,
        ...(draft.type === 'loan' ? [LOAN_REQUIRED_DEP_KEY] : [FULL_PAYMENT_DEP_KEY, BUC_DEP_KEY, PRE_BUC_DEP_KEY]),
        ...draft.steps.slice(0, stepIdx).map((s) => s.key).filter(Boolean),
        ...draft.steps.slice(0, stepIdx).filter((s) => s.actionType === 'toggle_yes_no').map((s) => yesAnswerDependency(s.key)),
        ...otherSteps.map((s) => `${otherType}:${s.key}`),
      ].filter((k) => !step.dependsOnSteps.includes(k))
    : [];
  const unblocks = step ? draft.steps.filter((s) => s.dependsOnSteps.includes(step.key)).map((s) => s.title) : [];

  const act = String(step?.actionType || 'mark_done');
  const needsDoc = ['upload_document', 'generate_document', 'send_email', 'loan_sanction_form'].includes(act);
  const docLabel = act === 'generate_document' ? 'Document template' : act === 'send_email' ? 'Email template' : 'Document';
  const docOptions = useMemo(() => {
    const src = act === 'generate_document' ? docTemplateOptions : act === 'send_email' ? emailTemplateOptions : act === 'upload_document' || act === 'loan_sanction_form' ? uploadDocKeyOptions : [];
    const set = new Set<string>([...(act === 'upload_document' ? ['sale_deed_draft', 'fitout_access_signed', 'fitout_condition_checklist', 'maintenance_cheque', 'final_possession_ack'] : []), ...src]);
    if (step?.docKey) set.add(step.docKey);
    return Array.from(set).filter(Boolean);
  }, [act, docTemplateOptions, emailTemplateOptions, step?.docKey, uploadDocKeyOptions]);

  const payload = (d: Draft) => ({
    name: d.name.trim(),
    type: d.type,
    version: Number(d.version || 1),
    active: !!d.active,
    description: d.description.trim(),
    assignedProjectIds: d.assignedProjectIds,
    schemaMode: 'step_only_v1',
    steps: stepsForSave(d.steps),
  });

  const save = async (): Promise<string | null> => {
    setMsg(null);
    if (errors.size) {
      const first = Array.from(errors.keys())[0];
      if (first >= 0) select(first);
      setMsg({ ok: false, text: errors.get(first) || 'Fix the marked steps first.' });
      return null;
    }
    if (!currentUser?.email) {
      setMsg({ ok: false, text: 'Sign in again to save.' });
      return null;
    }
    const body = { ...payload(draft), updatedAt: serverTimestamp(), updatedBy: currentUser.email };
    if (selectedId) {
      await updateDoc(doc(db, 'officeWorkflowDefinitions', selectedId), body);
      setBase(draft);
      return selectedId;
    }
    const ref = await addDoc(collection(db, 'officeWorkflowDefinitions'), { ...body, createdAt: serverTimestamp(), createdBy: currentUser.email });
    setSelectedId(ref.id);
    setBase(draft);
    return ref.id;
  };

  const onSave = async () => {
    setBusy('save');
    try {
      if (await save()) setMsg({ ok: true, text: 'Saved. Projects keep the published version until you publish.' });
    } catch (err: any) {
      setMsg({ ok: false, text: err?.message || "Couldn't save the workflow." });
    } finally {
      setBusy('');
    }
  };

  // Publish = save, then copy to every assigned project (what "Apply to projects" did) and record it.
  const nextVersion = row?.publishedAt ? Math.max(Number(draft.version || 1), Number(row?.publishedVersion || row?.version || 1) + 1) : Number(draft.version || 1);
  const onPublish = async () => {
    setMsg(null);
    if (!draft.assignedProjectIds.length) {
      setPanel('details');
      setMsg({ ok: false, text: 'Pick at least one project to publish to.' });
      return;
    }
    setBusy('publish');
    try {
      const published: Draft = { ...draft, version: nextVersion };
      setDraft(published);
      const id = await (async () => {
        const v = validateWorkflow(published.name, published.steps);
        if (v.size) {
          const first = Array.from(v.keys())[0];
          if (first >= 0) select(first);
          setMsg({ ok: false, text: v.get(first) || 'Fix the marked steps first.' });
          return null;
        }
        const body = { ...payload(published), updatedAt: serverTimestamp(), updatedBy: currentUser.email };
        if (selectedId) {
          await updateDoc(doc(db, 'officeWorkflowDefinitions', selectedId), body);
          return selectedId;
        }
        const ref = await addDoc(collection(db, 'officeWorkflowDefinitions'), { ...body, createdAt: serverTimestamp(), createdBy: currentUser.email });
        setSelectedId(ref.id);
        return ref.id;
      })();
      if (!id) return;
      const p = payload(published);
      const batch = writeBatch(db);
      published.assignedProjectIds.forEach((pid) => {
        batch.set(doc(db, 'projects', pid, 'workflowDefinitions', id), { ...p, id, sourceWorkflowId: id, appliedAt: serverTimestamp(), appliedBy: currentUser.email, updatedAt: serverTimestamp(), updatedBy: currentUser.email }, { merge: true });
        batch.set(doc(db, 'projects', pid), { ...(published.type === 'loan' ? { officeLoanWorkflowDefinitionId: id } : { officeMainWorkflowDefinitionId: id }), updatedAt: serverTimestamp(), updatedBy: currentUser.email }, { merge: true });
      });
      const history = [
        { version: published.version, at: Date.now(), by: currentUser.email, steps: published.steps.length, projects: projectNames },
        ...(Array.isArray(row?.history) ? row.history : []),
      ].slice(0, 20);
      batch.set(doc(db, 'officeWorkflowDefinitions', id), { publishedAt: serverTimestamp(), publishedVersion: published.version, publishedBy: currentUser.email, history }, { merge: true });
      await batch.commit();
      setBase(published);
      setMsg({ ok: true, text: `Published v${published.version} to ${projectNames.join(', ')}.` });
    } catch (err: any) {
      setMsg({ ok: false, text: err?.message || "Couldn't publish the workflow." });
    } finally {
      setBusy('');
    }
  };

  const status = !selectedId
    ? { tone: 'warn', text: 'New, not saved' }
    : dirty
      ? { tone: 'warn', text: 'Unsaved changes' }
      : savedNotPublished
        ? { tone: 'warn', text: 'Saved, not published' }
        : publishedAt
          ? { tone: 'good', text: `Live · v${row?.publishedVersion || draft.version}${projectNames.length ? ` · ${projectNames.join(', ')}` : ''}` }
          : { tone: 'mute', text: 'Not published from this screen yet' };

  const stepRow = (s: WfStep, i: number) => {
    const err = errors.get(i);
    const info = actionInfo(s.actionType);
    const deps = s.dependsOnSteps.length;
    return (
      <li
        key={`${s.key}-${i}`}
        className={`ows-step${i === stepIdx ? ' on' : ''}${s.enabled === false ? ' off' : ''}${err ? ' bad' : ''}${dragIdx === i ? ' dragging' : ''}`}
        draggable={view === 'order' && canEdit}
        onDragStart={() => setDragIdx(i)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => {
          if (dragIdx != null) move(dragIdx, i);
          setDragIdx(null);
        }}
        onDragEnd={() => setDragIdx(null)}
      >
        <button onClick={() => select(i)} aria-current={i === stepIdx}>
          {view === 'order' ? <span className="grip" aria-hidden="true"><Ic d={I.grip} size={13} /></span> : null}
          <span className="n num">{i + 1}</span>
          <span className="body">
            <span className="t">{s.title || 'Untitled step'}</span>
            <span className="sub">{info.short}{s.enabled === false ? ' · off' : ''}{!s.required ? ' · optional' : ''}</span>
          </span>
          {deps ? <span className="ows-wait" title={`Waits for ${deps} step${deps === 1 ? '' : 's'} or gate${deps === 1 ? '' : 's'}`} aria-label={`Waits for ${deps}`}>{deps}</span> : null}
          {err ? <span className="ows-dot bad" title={err} aria-label="Has a problem" /> : !sameStep(s, base.steps.find((b) => b.key === s.key)) ? <span className="ows-dot" title="Changed since last save" aria-label="Changed" /> : null}
        </button>
      </li>
    );
  };

  const workflowsOf = (t: 'main' | 'loan') => rows.filter((r) => (String(r.type) === 'loan' ? 'loan' : 'main') === t);

  return (
    <div className="ot ows">
      {loadError ? <div className="ot-alert" role="alert">{loadError}</div> : null}
      <header className="ows-head">
        <div className="ows-title">
          <div className="row">
            <h2 className="sr">{draft.name || 'New workflow'}</h2>
            {/* Workflows sit in the heading line: one pill each (there are only a few), status as a dot */}
            <nav className="ows-pills" aria-label="Workflows">
              {loading ? <span className="faint ows-pad">Loading workflows…</span> : null}
              {!loading && !rows.length ? <span className="faint ows-pad">No workflows yet. Use New to start from a template.</span> : null}
              {(['main', 'loan'] as const).flatMap((t) => workflowsOf(t)).map((r) => {
                const pub = toMs(r.publishedAt);
                const pending = pub && toMs(r.updatedAt) > pub + 1500;
                const steps = Array.isArray(r.steps) ? r.steps.length : 0;
                const projects = (r.assignedProjectIds || []).length;
                const state = pending ? 'Changes not published' : pub ? `Live in ${projects} project${projects === 1 ? '' : 's'}` : 'Not tracked yet';
                return (
                  <button
                    key={r.id}
                    className={`ows-pill${r.id === selectedId ? ' on' : ''}`}
                    aria-current={r.id === selectedId ? 'true' : undefined}
                    onClick={() => open(r)}
                    title={`${steps} steps · v${Number(r.publishedVersion || r.version || 1)}${r.active === false ? ' · inactive' : ''} · ${state}`}
                  >
                    <i className={`dot ${pending ? 'warn' : pub ? 'good' : ''}`} aria-hidden="true" />
                    <span className="k">{String(r.type) === 'loan' ? 'Loan' : 'Sales'}</span>
                    <span className="t">{String(r.name || r.id)}</span>
                    <span className="v num">v{Number(r.publishedVersion || r.version || 1)}</span>
                    <span className="sr"> · {state}</span>
                  </button>
                );
              })}
              {!selectedId && !loading ? <span className="ows-pill on" aria-current="true"><i className="dot" aria-hidden="true" /><span className="t">{draft.name || 'New workflow'}</span><span className="v">not saved</span></span> : null}
              <div className="ows-new">
                <button className="ows-pill add" aria-expanded={newMenu} onClick={() => setNewMenu((v) => !v)} disabled={!canEdit}><Ic d={I.plus} /> New</button>
                {newMenu ? (
                  <div className="ows-menu" role="menu">
                    <button role="menuitem" onClick={() => startNew({ ...salesPreset(), assignedProjectIds: [] })}>New sales workflow (template)</button>
                    <button role="menuitem" onClick={() => startNew({ ...loanPreset(), assignedProjectIds: [] })}>New loan workflow (template)</button>
                    <button role="menuitem" onClick={() => startNew({ ...EMPTY })}>Blank workflow</button>
                    {draft.type === 'main' && draft.steps.length ? <button role="menuitem" onClick={() => { const r = withDeedPossessionFlow(draft.steps); setSteps(() => r.steps); select(r.focus); setNewMenu(false); }}>Add deed and possession steps to this workflow</button> : null}
                  </div>
                ) : null}
              </div>
            </nav>
            <span className={`ot-chip c-${status.tone}`}>{status.text}</span>
          </div>
          <p>
            {draft.type === 'loan' ? 'Loan workflow' : 'Sales workflow'} · {draft.steps.length} steps
            {publishedAt ? ` · last published ${dateTime(publishedAt)}` : ''}
            {projectNames.length ? ` · ${projectNames.join(', ')}` : ' · no projects picked'}
          </p>
        </div>
        <div className="ows-actions">
          <button className="ou-ibtn ows-ib" aria-label="Workflow details" title="Workflow details" aria-pressed={panel === 'details'} onClick={() => setPanel(panel === 'details' ? 'step' : 'details')}><Ic d={I.gear} /></button>
          <button className="ou-ibtn ows-ib" aria-label="Version history" title="Version history" aria-pressed={panel === 'history'} onClick={() => setPanel(panel === 'history' ? 'step' : 'history')} disabled={!row}><Ic d={I.clock} /></button>
          {dirty && selectedId && canEdit ? <button className="ows-link" onClick={() => row && open(row)}>Discard changes</button> : null}
          {(dirty || !selectedId) && canEdit ? <button className="ot-btn sm" onClick={onSave} disabled={!!busy}>{busy === 'save' ? 'Saving…' : selectedId ? 'Save draft' : 'Create workflow'}</button> : null}
          <button className="ot-btn dark" onClick={onPublish} disabled={!canEdit || !!busy}>
            {busy === 'publish' ? 'Publishing…' : `Publish v${nextVersion}${draft.assignedProjectIds.length ? ` to ${draft.assignedProjectIds.length} project${draft.assignedProjectIds.length === 1 ? '' : 's'}` : ''}`}
          </button>
        </div>
      </header>
      {msg ? <div className={`ows-msg ${msg.ok ? 'ok' : 'bad'}`} role={msg.ok ? 'status' : 'alert'}>{msg.text}</div> : null}

      <div ref={gridRef} style={gridH ? ({ ['--ows-h' as any]: `${gridH}px` } as React.CSSProperties) : undefined} className={`ows-grid${expanded && view === 'flow' ? ' expanded' : ''}`}>

        {/* Steps */}
        <section className="ows-steps" aria-label="Steps">
          <div className="ows-steps-hd">
            <div className="ows-seg" role="group" aria-label="Group steps">
              <button aria-pressed={view === 'stage'} onClick={() => setView('stage')}>By stage</button>
              <button aria-pressed={view === 'order'} onClick={() => { setView('order'); setExpanded(false); }}>In order</button>
              <button aria-pressed={view === 'flow'} onClick={() => setView('flow')}>Flow</button>
            </div>
            {view === 'flow' ? (
              <button className="ot-btn sm" aria-pressed={expanded} onClick={() => setExpanded((v) => !v)} title={expanded ? 'Show the step editor again' : 'Hide the editor to give the chart the full width'}>
                {expanded ? 'Show editor' : 'Expand'}
              </button>
            ) : null}
            <div className="ows-new">
              <button className="ot-btn sm" onClick={addStep} disabled={!canEdit}><Ic d={I.plus} /> Add step</button>
            </div>
          </div>
          {errors.get(-1) ? <p className="ows-err">{errors.get(-1)}</p> : null}
          {!draft.steps.length ? <p className="faint ows-pad">No steps yet. Add one, or start from a template with + on the left.</p> : null}
          {view === 'flow' ? (
            <FlowChart
              steps={draft.steps}
              stages={stages}
              selected={stepIdx}
              onSelect={(i) => { select(i); setExpanded(false); }}
              otherLabel={depLabel}
            />
          ) : view === 'order' ? (
            <ol className="ows-flat">{draft.steps.map((s, i) => stepRow(s, i))}</ol>
          ) : (
            STAGE_OPTIONS.filter((st) => stages.includes(st.key)).map((st, si) => {
              const idxs = draft.steps.map((_, i) => i).filter((i) => stages[i] === st.key);
              return (
                <div key={st.key} className="ows-stage">
                  <div className="ows-stage-hd"><span className="badge num">{si + 1}</span><b>{st.title}</b><span className="faint">{idxs.length} step{idxs.length === 1 ? '' : 's'}</span></div>
                  <ol className="ows-rail">{idxs.map((i) => stepRow(draft.steps[i], i))}</ol>
                </div>
              );
            })
          )}
          {view === 'order' && draft.steps.length > 1 ? <p className="faint ows-hint">Drag steps by the handle to reorder. A step can only wait for steps above it.</p> : null}
        </section>

        {/* Editor */}
        <aside className="ows-editor" aria-label={panel === 'details' ? 'Workflow details' : panel === 'history' ? 'Version history' : 'Step editor'}>
          {panel === 'details' ? (
            <div className="ows-form">
              <div className="ows-ed-hd"><span className="ot-lbl">Workflow details</span><button className="ou-ibtn" aria-label="Close" onClick={() => setPanel('step')}><Ic d={I.x} /></button></div>
              <label className="ows-f"><span>Name</span><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} disabled={!canEdit} /></label>
              <div className="ows-2">
                <label className="ows-f"><span>Type</span>
                  <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value === 'loan' ? 'loan' : 'main' })} disabled={!canEdit}>
                    <option value="main">Sales</option>
                    <option value="loan">Loan</option>
                  </select>
                </label>
                <label className="ows-f"><span>Version</span><input type="number" min={1} value={draft.version} onChange={(e) => setDraft({ ...draft, version: Number(e.target.value || 1) })} disabled={!canEdit} /></label>
              </div>
              <label className="ows-f"><span>Description</span><textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} disabled={!canEdit} /></label>
              <label className="ows-check"><input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} disabled={!canEdit} /> Active</label>
              <div className="ows-f"><span>Projects that use it</span>
                <div className="ows-projects">
                  {(projects || []).map((p: any) => (
                    <label key={p.id} className="ows-check">
                      <input type="checkbox" checked={draft.assignedProjectIds.includes(p.id)} disabled={!canEdit} onChange={(e) => setDraft({ ...draft, assignedProjectIds: e.target.checked ? [...draft.assignedProjectIds, p.id] : draft.assignedProjectIds.filter((x) => x !== p.id) })} />
                      {p.name || p.id}
                    </label>
                  ))}
                </div>
                <small className="faint">Publishing copies this workflow into each picked project and makes it that project's {draft.type === 'loan' ? 'loan' : 'sales'} workflow.</small>
              </div>
              {draft.type === 'main' ? <button className="ot-btn sm" onClick={() => { const r = withDeedPossessionFlow(draft.steps); setSteps(() => r.steps); select(r.focus); }} disabled={!canEdit || !draft.steps.length}>Add deed and possession steps</button> : null}
              <button className="ot-btn sm" onClick={() => startNew({ ...EMPTY })} disabled={!canEdit}>Clear and start a blank workflow</button>
            </div>
          ) : panel === 'history' ? (
            <div className="ows-form">
              <div className="ows-ed-hd"><span className="ot-lbl">Version history</span><button className="ou-ibtn" aria-label="Close" onClick={() => setPanel('step')}><Ic d={I.x} /></button></div>
              {Array.isArray(row?.history) && row.history.length ? (
                <ol className="ows-hist">
                  {row.history.map((h: any, i: number) => (
                    <li key={i}><b>v{h.version}</b><span>{dateTime(Number(h.at))}</span><span className="faint">{h.by} · {h.steps} steps{Array.isArray(h.projects) && h.projects.length ? ` · ${h.projects.join(', ')}` : ''}</span></li>
                  ))}
                </ol>
              ) : (
                <p className="faint">No publishes recorded yet. History starts with the next publish.</p>
              )}
            </div>
          ) : !step ? (
            <p className="faint ows-pad">Pick a step to edit it.</p>
          ) : (
            <div className="ows-form">
              <div className="ows-ed-hd sticky">
                <div>
                  <span className="ot-lbl">{STAGE_OPTIONS.find((s) => s.key === stages[stepIdx])?.title} · step {stepIdx + 1}</span>
                  <h3>{step.title || 'Untitled step'}</h3>
                </div>
                <div className="ows-tools">
                  <button className="ou-ibtn" aria-label="Move up" title="Move up" onClick={() => move(stepIdx, stepIdx - 1)} disabled={!canEdit || stepIdx === 0}><Ic d={I.up} /></button>
                  <button className="ou-ibtn" aria-label="Move down" title="Move down" onClick={() => move(stepIdx, stepIdx + 1)} disabled={!canEdit || stepIdx >= draft.steps.length - 1}><Ic d={I.down} /></button>
                  <button className="ou-ibtn" aria-label="Duplicate step" title="Duplicate" onClick={duplicate} disabled={!canEdit}><Ic d={I.copy} /></button>
                  <button className="ou-ibtn" aria-label="Remove step" title="Remove step" onClick={() => setConfirmDelete(true)} disabled={!canEdit}><Ic d={I.trash} /></button>
                </div>
              </div>
              {errors.get(stepIdx) ? <p className="ows-err" role="alert">{errors.get(stepIdx)}</p> : null}

              <label className="ows-f"><span>Title</span>
                <input value={step.title} disabled={!canEdit} onChange={(e) => {
                  const v = e.target.value;
                  // New steps follow the title; published keys never change by themselves.
                  const follow = !publishedKeys.has(step.key) && (!step.key || step.key === normalizeKey(step.title));
                  patchStep({ title: v, ...(follow ? { key: normalizeKey(v) || step.key } : {}) });
                }} />
              </label>
              <div className="ows-f"><span>Key</span>
                {keyLocked ? (
                  <div className="ows-key"><Ic d={I.lock} size={13} /><code>{step.key}</code><button className="ows-link" onClick={() => setKeyUnlocked(true)} disabled={!canEdit}>Change</button></div>
                ) : (
                  <>
                    <input value={step.key} disabled={!canEdit} onChange={(e) => patchStep({ key: e.target.value })} />
                    {publishedKeys.has(base.steps[stepIdx]?.key || '') ? <small className="ows-warnline">Units that finished this step are recorded under the old key. Changing it makes them show the step as not done.</small> : null}
                  </>
                )}
              </div>
              <label className="ows-f"><span>Help text</span><input value={step.helpText} disabled={!canEdit} onChange={(e) => patchStep({ helpText: e.target.value })} placeholder="Shown under the step in the booking drawer" /></label>

              <div className="ows-2">
                <label className="ows-f"><span>Stage</span>
                  <select value={step.stage} disabled={!canEdit || draft.type === 'loan'} onChange={(e) => patchStep({ stage: e.target.value })}>
                    <option value="">Auto ({STAGE_OPTIONS.find((s) => s.key === stages[stepIdx])?.title})</option>
                    {STAGE_OPTIONS.filter((s) => s.key !== 'loan').map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
                  </select>
                </label>
                <label className="ows-f"><span>What the office does</span>
                  <select value={act} disabled={!canEdit} onChange={(e) => {
                    const v = e.target.value;
                    const needsField = ['toggle_yes_no', 'checklist', 'input_value'].includes(v) && !String(step.fieldKey || '').trim();
                    patchStep({ actionType: v, ...(needsField ? { fieldKey: normalizeKey(step.title || step.key) || step.key } : {}) });
                  }}>
                    {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </label>
              </div>
              {needsDoc ? (
                <label className="ows-f"><span>{docLabel}</span>
                  <select value={step.docKey} disabled={!canEdit} onChange={(e) => patchStep({ docKey: e.target.value })}>
                    <option value="">{docOptions.length ? 'Pick one' : 'No documents set up yet'}</option>
                    {docOptions.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </label>
              ) : null}
              {['toggle_yes_no', 'checklist', 'input_value'].includes(act) ? (
                <div className="ows-2">
                  <label className="ows-f"><span>Field key</span><input value={step.fieldKey} disabled={!canEdit} onChange={(e) => patchStep({ fieldKey: e.target.value })} /></label>
                  {act === 'input_value' ? (
                    <label className="ows-f"><span>Input type</span>
                      <select value={step.inputType || 'text'} disabled={!canEdit} onChange={(e) => patchStep({ inputType: e.target.value })}>
                        <option value="text">Text</option><option value="date">Date</option><option value="number">Number</option><option value="textarea">Long text</option>
                      </select>
                    </label>
                  ) : <span />}
                </div>
              ) : null}
              {act === 'add_payment' ? (
                <label className="ows-f"><span>Payment type</span>
                  <select value={step.paymentType || 'both'} disabled={!canEdit} onChange={(e) => patchStep({ paymentType: e.target.value })}>
                    <option value="both">Customer or GST</option><option value="customer">Customer</option><option value="gst">GST</option>
                  </select>
                </label>
              ) : null}

              <div className="ows-ready">
                <div className="ows-ready-hd">
                  Ready when
                  <div className="ows-seg sm" role="group" aria-label="Ready when">
                    <button aria-pressed={step.dependsMode !== 'any'} onClick={() => patchStep({ dependsMode: 'all' })} disabled={!canEdit}>all</button>
                    <button aria-pressed={step.dependsMode === 'any'} onClick={() => patchStep({ dependsMode: 'any' })} disabled={!canEdit}>any</button>
                  </div>
                  of these are done:
                </div>
                <div className="ows-chips">
                  {step.dependsOnSteps.length ? step.dependsOnSteps.map((d) => (
                    <span key={d} className={`ows-chip${isGate(d) ? ' gate' : ''}`}>
                      {depLabel(d)}
                      <button aria-label={`Remove ${depLabel(d)}`} onClick={() => patchStep({ dependsOnSteps: step.dependsOnSteps.filter((x) => x !== d) })} disabled={!canEdit}><Ic d={I.x} size={11} /></button>
                    </span>
                  )) : <span className="faint">Nothing. It is ready as soon as the workflow starts.</span>}
                </div>
                <label className="ows-add">
                  <span className="sr">Add a step or gate to wait for</span>
                  <select value="" disabled={!canEdit || !depOptions.length} onChange={(e) => e.target.value && patchStep({ dependsOnSteps: [...step.dependsOnSteps, e.target.value] })}>
                    <option value="">{depOptions.length ? '+ Add a step or gate' : 'Nothing else to add'}</option>
                    {depOptions.map((k) => <option key={k} value={k}>{depLabel(k)}</option>)}
                  </select>
                </label>
                {unblocks.length ? <p className="faint ows-unblocks">Unblocks: {unblocks.join(', ')}</p> : null}
              </div>

              <div className="ows-2">
                <label className="ows-f"><span>Due within (days)</span><input type="number" min={0} value={step.dueInDays} disabled={!canEdit} onChange={(e) => patchStep({ dueInDays: e.target.value })} placeholder="No deadline" /></label>
                <label className="ows-f"><span>Who does it</span>
                  <select value={step.defaultRole} disabled={!canEdit} onChange={(e) => patchStep({ defaultRole: e.target.value })}>
                    {ROLE_OPTIONS.map((r) => <option key={r.value || 'any'} value={r.value}>{r.label}</option>)}
                  </select>
                </label>
              </div>
              <small className="faint" style={{ marginTop: -6 }}>Due days and who does it are saved with the step; tasks and reminders will use them later.</small>

              <fieldset className="ows-flags">
                <legend>Rules and where it shows</legend>
                <label className="ows-check"><input type="checkbox" checked={step.enabled !== false} disabled={!canEdit} onChange={(e) => patchStep({ enabled: e.target.checked })} /> Enabled</label>
                <label className="ows-check"><input type="checkbox" checked={!!step.required} disabled={!canEdit} onChange={(e) => patchStep({ required: e.target.checked })} /> Required</label>
                <label className="ows-check"><input type="checkbox" checked={!!step.allowSkip} disabled={!canEdit} onChange={(e) => patchStep({ allowSkip: e.target.checked })} /> Allow skip</label>
                <label className="ows-check"><input type="checkbox" checked={step.showInDrawer !== false} disabled={!canEdit} onChange={(e) => patchStep({ showInDrawer: e.target.checked })} /> Show in booking drawer</label>
                <label className="ows-check"><input type="checkbox" checked={step.showInDashboard !== false} disabled={!canEdit} onChange={(e) => patchStep({ showInDashboard: e.target.checked })} /> Show on Today and unit page</label>
                <label className="ows-check"><input type="checkbox" checked={!!step.keepActive} disabled={!canEdit} onChange={(e) => patchStep({ keepActive: e.target.checked })} /> Keep active after done</label>
                <label className="ows-check"><input type="checkbox" checked={!!step.showInOtherWorkflow} disabled={!canEdit} onChange={(e) => patchStep({ showInOtherWorkflow: e.target.checked })} /> Let the {draft.type === 'loan' ? 'sales' : 'loan'} workflow wait for it</label>
              </fieldset>

              <div className="ows-preview">
                <span className="ot-lbl">In the booking drawer</span>
                <div className="card">
                  <div><b>{step.title || 'Step title'}</b><span className="faint">{step.helpText || actionInfo(act).label}</span></div>
                  <span className="ot-btn sm">{actionInfo(act).button}</span>
                </div>
              </div>

              <div className="ows-del">
                {confirmDelete ? (
                  <div className="ows-confirm" role="alert">
                    <p>{publishedKeys.has(step.key) ? 'This step is live. Units that finished it keep the record, but the step leaves their list after you publish.' : 'Remove this step from the workflow?'}{unblocks.length ? ` It is also taken out of "Ready when" for ${unblocks.length} other step${unblocks.length === 1 ? '' : 's'}.` : ''}</p>
                    <div><button className="ot-btn sm" onClick={() => setConfirmDelete(false)}>Keep it</button><button className="ot-btn sm ows-danger" onClick={remove}>Remove step</button></div>
                  </div>
                ) : (
                  <button className="ows-link danger" onClick={() => setConfirmDelete(true)} disabled={!canEdit}><Ic d={I.trash} size={13} /> Remove step</button>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function sameStep(a: WfStep, b: WfStep | undefined) {
  if (!b) return false;
  return JSON.stringify(stepsForSave([a])[0]) === JSON.stringify(stepsForSave([b])[0]);
}
